import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { useUtmCapture } from "@/hooks/useUtmCapture";

interface Props {
  children: ReactNode;
}

/**
 * Minimal layout for landing pages — strips full navigation
 * to eliminate distractions and keep focus on conversion.
 */
const LandingLayout = ({ children }: Props) => {
  useUtmCapture();
  return (
    <div className="min-h-screen flex flex-col relative bg-background">
    {/* Global atmospheric warmth — matches main site */}
    <div className="pointer-events-none fixed inset-0 z-0">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[140%] h-[800px] bg-[radial-gradient(ellipse_at_top_center,hsl(32_35%_52%_/_0.035)_0%,hsl(32_35%_52%_/_0.012)_40%,transparent_70%)]" />
      <div className="absolute top-[35%] left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[radial-gradient(ellipse_at_center,hsl(32_35%_52%_/_0.015)_0%,transparent_55%)] blur-[140px]" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[130%] h-[500px] bg-[radial-gradient(ellipse_at_bottom_center,hsl(25_20%_18%_/_0.025)_0%,transparent_65%)]" />
    </div>

    {/* Minimal header — logo only, no menu */}
    <motion.header
      className="fixed top-0 left-0 right-0 z-50 px-6 py-5"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 0.68, 0.35, 1] }}
    >
      <div className="max-w-5xl mx-auto flex items-center justify-between">
        <Link
          to="/"
          className="font-heading italic text-[1.15rem] text-foreground tracking-tight hover:text-accent transition-colors duration-500"
        >
          Sprankelien
        </Link>
        <span className="text-[10px] font-body font-semibold uppercase tracking-[0.3em] text-muted-foreground/40">
          Praktijk voor kinderbegeleiding
        </span>
      </div>
    </motion.header>

    {/* Page content */}
    <main className="flex-1 relative z-[1]">{children}</main>

    {/* Minimal footer */}
    <footer className="relative z-[1] py-10 px-6 text-center">
      <div className="space-y-3">
        <p className="text-[12px] text-muted-foreground/35 font-body">
          © {new Date().getFullYear()} Praktijk Sprankelien — Ramsel
        </p>
        <div className="flex items-center justify-center gap-6">
          <Link
            to="/"
            className="text-[11px] text-muted-foreground/40 font-body hover:text-accent/60 transition-colors duration-500"
          >
            Website
          </Link>
          <Link
            to="/contact"
            className="text-[11px] text-muted-foreground/40 font-body hover:text-accent/60 transition-colors duration-500"
          >
            Contact
          </Link>
        </div>
      </div>
    </footer>
  </div>
  );
};

export default LandingLayout;
